import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App';
import { initAnalytics } from './analytics';
import { SENTRY_IGNORE_ERRORS } from './sentryIgnore';
import './index.css';

// One-shot migration of localStorage keys from the CollabAI brand to Symphonate.
// Keeps existing sessions and onboarding state intact across the rebrand. Safe
// to leave in place: after the value moves, the old key is removed, so it's a
// no-op on every subsequent load.
(function migrateLegacyBrandStorage() {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    const renames = {
      'collabai-token': 'symphonate-token',
      'collabai-user': 'symphonate-user',
      'collabai-workspace': 'symphonate-workspace',
      'collabai-seen-counts': 'symphonate-seen-counts',
      'collabai-last-room': 'symphonate-last-room',
    };
    for (const [oldKey, newKey] of Object.entries(renames)) {
      const value = localStorage.getItem(oldKey);
      if (value === null) continue;
      if (localStorage.getItem(newKey) === null) localStorage.setItem(newKey, value);
      localStorage.removeItem(oldKey);
    }
    const onboardingPrefix = 'collabai-onboarding-seen-';
    const newOnboardingPrefix = 'symphonate-onboarding-seen-';
    const onboardingKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(onboardingPrefix)) onboardingKeys.push(key);
    }
    for (const oldKey of onboardingKeys) {
      const newKey = newOnboardingPrefix + oldKey.slice(onboardingPrefix.length);
      const value = localStorage.getItem(oldKey);
      if (value !== null && localStorage.getItem(newKey) === null)
        localStorage.setItem(newKey, value);
      localStorage.removeItem(oldKey);
    }
  } catch {
    // localStorage can throw under quota / private-mode; silently fall back.
  }
})();

// Inert when REACT_APP_SENTRY_DSN is unset (CRA only exposes REACT_APP_* vars).
if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.NODE_ENV,
    // Known third-party noise (embedded-browser JS bridges, email link
    // scanners) that no code change of ours can prevent — see sentryIgnore.js.
    // Merged with the SDK's own default inbound filters.
    ignoreErrors: SENTRY_IGNORE_ERRORS,
  });
}

// Tag the document with the deploy environment so CSS can give the test
// deployment a distinct (pale pastel-yellow) background — an at-a-glance cue
// that you're not looking at production. REACT_APP_ENV is baked at build time
// by the CI deploy workflows ("test" / "production"); empty in local/dev builds.
if (process.env.REACT_APP_ENV) {
  document.documentElement.setAttribute('data-env', process.env.REACT_APP_ENV);
}

// Inert when REACT_APP_POSTHOG_KEY is unset — see analytics.js.
initAnalytics();

// Register the (network-passthrough) service worker so the app is installable
// as a PWA — Chrome/Edge/Android only fire `beforeinstallprompt` when the page
// controls a service worker. Production only: a worker under the CRA dev server
// invites stale-asset headaches, and it's needless in tests.
if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register(`${process.env.PUBLIC_URL}/service-worker.js`).catch(() => {
      // Registration failures (unsupported browser, insecure origin) are
      // non-fatal — the app still works, it just isn't installable.
    });
  });
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
